<template>
    <div>
        <div class="d-flex justify-content-center align-items-center"  ref="mapContainer">
            <div v-if="loading"  class="d-flex flex-column align-items-center justify-content-center py-4 text-dark">
                <i class="fa-solid fa-spinner fa-spin fa-4x"></i>
            </div>
            <vuevectormap
                v-else
                :key="mapDrawKey"
                height="400"
                width="100%"
                map="world"
                :options="mapOptions"
            />
        </div>
        <div class="d-flex justify-content-center align-items-center mt-4" v-if="!loading">
            <Tag severity="info">
                <div class="flex items-center gap-2 px-1">
                    <i class="fa fa-globe mr-2"></i>
                    <span><strong>Country Count: {{ whitelistCountries.length }}</strong></span>
                </div>
            </Tag>
            <span class="mx-2">|</span>
            <Tag severity="info">
                <div class="flex items-center gap-2 px-1">
                    <i class="fa fa-signal mr-2"></i>
                    <span><strong>Network Count: {{ whitelistNetworkCount }}</strong></span>
                </div>
            </Tag>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import _ from 'lodash';
import  Tag from 'primevue/tag';
export default {
    name: "WhitelistMap",
    props: {
        whitelistId: {
            type: String,
            required: true,
        },
    },
    components: {Tag},
    emits: ["region-clicked", "fetch-error"],
    data() {
        return {
            mapDrawKey: "",
            whitelistCountries: [],
            loading: true,
            resizeObserver: null,
        };
    },
    computed: {
        themeColor() {
            return this.$themeSettings.themeColor;
        },
        countryLookup() {
            const lookup = {};
            (this.whitelistCountries || []).forEach(({ iso, countryName, mcc, networksCount }) => {
                if (!lookup[iso]) {
                    lookup[iso] = { countryName, entries: [] };
                }
                lookup[iso].entries.push({ mcc, networksCount });
            });
            return lookup;
        },
        whitelistRegions() {
            return (this.whitelistCountries || []).map(c => c.iso);
        },
        whitelistNetworkCount() {
            return 0 + (this.whitelistCountries || []).reduce((acc, c) => acc + c.networksCount, 0);
        },
        mapOptions() {
            return {
                selectedRegions: this.whitelistRegions,
                backgroundColor: '#FFFFFF',
                regionStyle: {
                    initial: {
                        fill: '#CFD8DC',
                        'fill-opacity': 1,
                        stroke: 'none',
                        'stroke-width': 0,
                        'stroke-opacity': 1,
                    },
                    hover: {
                        fill: this.themeColor,
                        'fill-opacity': 0.8,
                        cursor: 'pointer',
                    },
                    selected: {
                        fill: this.themeColor,
                    },
                    selectedHover: {
                        fill: this.themeColor,
                        'fill-opacity': 0.9,
                    },
                },
                onRegionTooltipShow: this.onRegionTooltipShow.bind(this),
                onRegionClick: this.onRegionClick.bind(this),
            };
        },
    },
    watch: {
        whitelistId: {
            immediate: true,
            handler(newId) {
                this.loadMapData();
            },
        },
        repaintKey(newKey) {
            this.mapDrawKey = `${this.whitelistId}-${newKey}`;
        },
    },
    mounted() {
        this.loadMapData();

        //Handle window resize
        this.resizeObserver = new ResizeObserver(
            _.debounce(() => {
                this.mapDrawKey = `${this.whitelistId}-${Date.now()}`;
            }, 250)
        );
        const el = this.$refs.mapContainer;
        if (el) this.resizeObserver.observe(el);
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        onRegionTooltipShow(event, tooltip, code) {
            const data = this.countryLookup[code];
            if (data) {
                let html = `<strong>${data.countryName}</strong><br/>`;
                data.entries.forEach(({ mcc, networksCount }) => {
                    html += `MCC ${mcc}: ${networksCount} network${networksCount > 1 ? 's' : ''}<br/>`;
                });
                tooltip.text(html, true);
            }
        },

        onRegionClick(event, code) {
            this.$emit("region-clicked", code);
        },
        loadMapData() {
            this.loading = true;
            axios
                .get(`/network-access/${this.whitelistId}/map`, {
                    headers: { Accept: "application/json" },
                })
                .then(({ data }) => {
                    this.whitelistCountries = data.data;
                })
                .catch((err) => {
                    this.$emit("fetch-error", {
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to load map data",
                        life: 3000,
                    });
                    this.whitelistCountries = [];
                })
                .finally(() => {
                    this.loading = false;
                    this.mapDrawKey = `${this.whitelistId}-${this.repaintKey}`;
                });
        },
    },
};
</script>

<style scoped>
</style>
